<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
    validator: value =>
      value.every(option => 'value' in option && 'label' in option),
  },
  placeholder: {
    type: String,
    default: '',
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  hasWarning: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const selectedValues = ref(
  Array.isArray(props.modelValue) ? [...props.modelValue] : []
);
const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);
const comboboxRef = ref(null);

const filteredOptions = computed(() => {
  const searchTerm = search.value.toLowerCase();
  return props.options.filter(option => {
    const haystack = `${option.label || ''} ${option.categoryLabel || ''}`;
    return haystack.toLowerCase().includes(searchTerm);
  });
});

const selectPlaceholder = computed(() => {
  return props.placeholder || t('COMBOBOX.PLACEHOLDER');
});

const selectedTags = computed(() => {
  return selectedValues.value.map(value => {
    const option = props.options.find(
      opt => String(opt.value) === String(value)
    );
    return option || { value, label: value };
  });
});

const emitSelection = next => {
  selectedValues.value = next;
  // Always emit a new array so parent v-model stays in sync (avoid shared-ref mutation).
  emit('update:modelValue', [...next]);
};

const toggleOption = option => {
  const current = Array.isArray(selectedValues.value)
    ? [...selectedValues.value]
    : [];
  const index = current.findIndex(
    value => String(value) === String(option.value)
  );
  if (index === -1) {
    current.push(option.value);
  } else {
    current.splice(index, 1);
  }
  emitSelection(current);
};

const removeTag = value => {
  const current = Array.isArray(selectedValues.value)
    ? [...selectedValues.value]
    : [];
  const index = current.findIndex(item => String(item) === String(value));
  if (index === -1) return;
  current.splice(index, 1);
  emitSelection(current);
};

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => dropdownRef.value?.focus());
  }
};

watch(
  () => props.modelValue,
  newValue => {
    selectedValues.value = Array.isArray(newValue) ? [...newValue] : [];
  }
);

defineExpose({
  toggleDropdown,
  open,
  disabled: props.disabled,
});
</script>

<template>
  <div
    ref="comboboxRef"
    class="relative w-full min-w-0"
    :class="{
      'cursor-not-allowed': disabled,
      'group/combobox': !disabled,
    }"
    @click.prevent
  >
    <OnClickOutside @trigger="open = false">
      <div
        class="flex flex-wrap items-center w-full gap-2 px-3 py-2 min-h-10 cursor-pointer reset-base outline outline-1 border-none border-0 outline-offset-[-1px] rounded bg-white dark:bg-slate-800 font-medium antialiased text-sm leading-5 text-n-slate-12 transition-all duration-500 ease-in-out"
        :class="{
          'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9':
            hasError,
          'outline-n-amber-8 dark:outline-n-amber-8':
            !hasError && hasWarning,
          'outline-n-slate-6 dark:outline-n-slate-6 hover:outline-n-slate-6 dark:hover:outline-n-slate-6':
            !hasError && !hasWarning && !open,
          'outline-n-brand dark:outline-n-brand':
            !hasError && !hasWarning && open,
          'cursor-not-allowed pointer-events-none opacity-50': disabled,
        }"
        @click="toggleDropdown"
      >
        <div
          v-for="tag in selectedTags"
          :key="tag.value"
          class="flex items-center justify-center max-w-full gap-1 px-2 py-0.5 rounded bg-n-slate-3 dark:bg-slate-700"
          @click.stop
        >
          <span
            v-if="tag.icon"
            :class="tag.icon"
            class="flex-shrink-0 size-3 text-n-slate-11"
          />
          <span class="flex-grow min-w-0 text-sm truncate text-n-slate-12">
            {{ tag.label }}
          </span>
          <span
            v-if="!disabled"
            class="flex-shrink-0 cursor-pointer i-lucide-x size-3 text-n-slate-11"
            @click="removeTag(tag.value)"
          />
        </div>
        <span
          v-if="selectedTags.length === 0"
          class="flex items-center text-sm text-n-slate-10 dark:text-n-slate-10"
        >
          {{ selectPlaceholder }}
        </span>
      </div>

      <ComboBoxDropdown
        ref="dropdownRef"
        :open="open"
        :options="filteredOptions"
        :search-value="search"
        :search-placeholder="searchPlaceholder"
        :empty-state="emptyState"
        multiple
        :selected-values="selectedValues"
        @update:search-value="search = $event"
        @select="toggleOption"
      />

      <p
        v-if="message"
        class="mt-1 mb-0 flex min-w-0 items-start gap-1.5 text-xs transition-all duration-500 ease-in-out"
        :class="{
          'text-n-ruby-9 dark:text-n-ruby-9': hasError,
          'text-n-amber-11 dark:text-n-amber-11': !hasError && hasWarning,
          'text-n-slate-11 dark:text-n-slate-11': !hasError && !hasWarning,
        }"
      >
        <span
          v-if="hasWarning && !hasError"
          class="i-lucide-triangle-alert mt-0.5 size-3.5 shrink-0"
          aria-hidden="true"
        />
        <span class="min-w-0">{{ message }}</span>
      </p>
    </OnClickOutside>
  </div>
</template>
